import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Select,
    DatePicker,
    Modal,
    message,
    Statistic,
    Row,
    Col,
    Typography,
    Progress,
    Tooltip,
    Alert,
    Divider,
    Descriptions,
    Spin
} from 'antd';
import {
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    ExportOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
    BarChartOutlined,
    DashboardOutlined,
    ProfileOutlined,
    DollarOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate, useHistory,Link } from 'react-router-dom';
import config from '../config';
import { useSelector } from 'react-redux';
import api from '../api/client';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const SlaughterWaste = () => {
    const navigate = useHistory();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [totals, setTotals] = useState({});
    const [summary, setSummary] = useState({});
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        farm: ''
    });
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [farms, setFarms] = useState([]);
    const currentUser = useSelector((state) => state.account.user);
    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;

    // Fetch farms
    const fetchFarms = async () => {
        try {
            const response = await api.get(`/farm`);
            setFarms(response.data);
        } catch (error) {
            console.error('Error fetching farms:', error);
        }
    };

    // Fetch data
    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            };

            const response = await api.get(`/api/slaughter/waste`, { params });
            setData(response.data.data);
            setTotals(response.data.totals);
            setSummary(response.data.summary);
            setPagination({ ...pagination, total: response.data.pagination.total });
        } catch (error) {
            message.error('Failed to fetch slaughter waste records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFarms();
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [pagination.page, filters]);

    const handleTableChange = (pagination) => {
        setPagination({
            page: pagination.current,
            limit: pagination.pageSize,
            total: pagination.total
        });
    };

    const columns = [
        {
            title: 'Tag Number',
            dataIndex: 'tagNumber',
            key: 'tagNumber',
            render: (text) => <Text strong>{text || 'N/A'}</Text>
        },
        {
            title: 'Animal',
            dataIndex: 'animalName',
            key: 'animalName',
            render: (text, record) => (
                <Tooltip title={`${record.breed || ''} - ${record.gender || ''}`}>
                    <Text>{text || record.tagNumber}</Text>
                </Tooltip>
            )
        },
        {
            title: 'Weight Before (kg)',
            dataIndex: 'weightBefore',
            key: 'weightBefore',
            render: (weight) => <Text strong>{weight} kg</Text>,
            sorter: (a, b) => a.weightBefore - b.weightBefore
        },
        {
            title: 'Weight After (kg)',
            dataIndex: 'weightAfter',
            key: 'weightAfter',
            render: (weight) => <Text>{weight} kg</Text>
        },
        {
            title: 'Waste Items',
            key: 'waste',
            render: (_, record) => (
                <Space wrap>
                    {record.hoovesWeight > 0 && <Text>Hooves: {record.hoovesWeight}kg</Text>}
                    {record.headWeight > 0 && <Text>Head: {record.headWeight}kg</Text>}
                    {record.skinWeight > 0 && <Text>Skin: {record.skinWeight}kg</Text>}
                    {record.internalOrgansWeight > 0 && <Text>Organs: {record.internalOrgansWeight}kg</Text>}
                </Space>
            )
        },
        {
            title: 'Waste %',
            dataIndex: 'wastePercentage',
            key: 'wastePercentage',
            render: (percent) => (
                <Progress
                    percent={parseFloat(percent)}
                    size="small"
                    status={parseFloat(percent) > 50 ? 'exception' : 'success'}
                    format={() => `${percent}%`}
                    style={{ width: 80 }}
                />
            )
        },
        {
            title: 'Sellable (kg)',
            dataIndex: 'sellableWeight',
            key: 'sellableWeight',
            render: (weight) => <Text style={{ color: '#52c41a' }}>{weight} kg</Text>
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const verified = Math.abs(record.totalWasteWeight + record.sellableWeight - record.weightBefore) <= 0.01;
                return <Text type={verified ? 'success' : 'danger'}>{verified ? '✓ Verified' : '✗ Mismatch'}</Text>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button type="text" icon={<InfoCircleOutlined />} onClick={() => setSelectedRecord(record)} />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <>
            <Card
                title="Slaughter Biological Management"
                extra={
                    <Space>
                        <Link to="/slaughter/add">
                            <Button type="primary" icon={<PlusOutlined />}>
                                Add Record
                            </Button>
                        </Link>
                        <Button icon={<ReloadOutlined />} onClick={fetchData}>
                            Refresh
                        </Button>
                        <Button icon={<ExportOutlined />}>Export</Button>
                    </Space>
                }
            >
                {/* Summary Statistics */}
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic title="Total Records" value={totals.totalRecords || 0} prefix={<BarChartOutlined />} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Biological Weight"
                                value={totals.totalWasteWeight || 0}
                                suffix="kg"
                                prefix={<CloseCircleOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Sellable Weight"
                                value={totals.totalSellableWeight || 0}
                                suffix="kg"
                                prefix={<DollarOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic title="Recovery Rate" value={summary.recoveryRate || 0} suffix="%" prefix={<DashboardOutlined />} />
                        </Card>
                    </Col>
                </Row>

                <Divider />

                {/* Filters */}
                <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Select Farm"
                            style={{ width: '100%' }}
                            allowClear
                            value={filters.farm}
                            onChange={(value) => setFilters({ ...filters, farm: value })}
                        >
                            {farms.map((farm) => (
                                <Option key={farm.id} value={farm.id}>
                                    {farm.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <RangePicker
                            style={{ width: '100%' }}
                            onChange={(dates) => {
                                if (dates) {
                                    setFilters({
                                        ...filters,
                                        startDate: dates[0].format('YYYY-MM-DD'),
                                        endDate: dates[1].format('YYYY-MM-DD')
                                    });
                                } else {
                                    setFilters({ ...filters, startDate: '', endDate: '' });
                                }
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <Button type="primary" onClick={fetchData} style={{ width: '100%' }}>
                            <SearchOutlined /> Apply Filters
                        </Button>
                    </Col>
                </Row>

                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="id"
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.limit,
                            total: pagination.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `Total ${total} records`
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 1200 }}
                        expandable={{
                            expandedRowRender: (record) => (
                                <div style={{ padding: '16px' }}>
                                    <Descriptions bordered size="small" column={2}>
                                        <Descriptions.Item label="Animal Name">
                                            <Text>{record.animalName || 'N/A'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Breed">
                                            <Text>{record.breed || 'N/A'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Gender">
                                            <Text>{record.gender || 'N/A'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Silo">
                                            <Text>{record.siloNumber || 'N/A'}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Hooves">
                                            <Text>{record.hoovesWeight} kg</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Head">
                                            <Text>{record.headWeight} kg</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Skin">
                                            <Text>{record.skinWeight} kg</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Internal Organs">
                                            <Text>{record.internalOrgansWeight} kg</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Total Biological Weight">
                                            <Text strong>{record.totalWasteWeight} kg</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Sellable">
                                            <Text strong style={{ color: '#52c41a' }}>
                                                {record.sellableWeight} kg
                                            </Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Verified">
                                            <Text
                                                type={
                                                    Math.abs(record.totalWasteWeight + record.sellableWeight - record.weightBefore) <= 0.01
                                                        ? 'success'
                                                        : 'danger'
                                                }
                                            >
                                                {Math.abs(record.totalWasteWeight + record.sellableWeight - record.weightBefore) <= 0.01
                                                    ? 'Yes'
                                                    : 'No'}
                                            </Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Sale Recorded">
                                            <Text type={record.saleRecorded ? 'success' : 'secondary'}>
                                                {record.saleRecorded ? 'Yes' : 'No'}
                                            </Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Remarks" span={2}>
                                            <Text>{record.remarks || 'N/A'}</Text>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </div>
                            )
                        }}
                    />
                </Spin>
            </Card>

            {/* View Details Modal */}
            <Modal
                title="Slaughter Biological Details"
                open={selectedRecord !== null}
                onCancel={() => setSelectedRecord(null)}
                footer={null}
                width={600}
            >
                {selectedRecord && (
                    <div>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Tag Number">
                                <Text>{selectedRecord.tagNumber || 'N/A'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Animal Type">
                                <Text>{selectedRecord.animalType}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Weight Before">
                                <Text>{selectedRecord.weightBefore} kg</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Weight After">
                                <Text>{selectedRecord.weightAfter} kg</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Slaughter Date">
                                <Text>{dayjs(selectedRecord.slaughterDate).format('DD/MM/YYYY HH:mm')}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Farm">
                                <Text>{farms.find((f) => f.id === selectedRecord.farm)?.name || 'N/A'}</Text>
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider>Biological Breakdown</Divider>

                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Card size="small">
                                    <Statistic title="Hooves" value={selectedRecord.hoovesWeight} suffix="kg" />
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card size="small">
                                    <Statistic title="Head" value={selectedRecord.headWeight} suffix="kg" />
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card size="small">
                                    <Statistic title="Skin" value={selectedRecord.skinWeight} suffix="kg" />
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card size="small">
                                    <Statistic title="Internal Organs" value={selectedRecord.internalOrgansWeight} suffix="kg" />
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card size="small" style={{ background: '#f6ffed' }}>
                                    <Statistic title="Total Biological Weight" value={selectedRecord.totalWasteWeight} suffix="kg" />
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card size="small" style={{ background: '#f6ffed' }}>
                                    <Statistic title="Sellable" value={selectedRecord.sellableWeight} suffix="kg" />
                                </Card>
                            </Col>
                        </Row>

                        {selectedRecord.saleDetails && (
                            <>
                                <Divider>Sale Details</Divider>
                                <Descriptions bordered column={2} size="small">
                                    <Descriptions.Item label="Sale Date">
                                        <Text>{dayjs(selectedRecord.saleDetails.saleDate).format('DD/MM/YYYY HH:mm')}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Price per kg">
                                        <Text>KES {selectedRecord.saleDetails.pricePerKg}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Total Price">
                                        <Text strong>KES {selectedRecord.saleDetails.totalPrice}</Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Buyer">
                                        <Text>{selectedRecord.saleDetails.buyerName || 'N/A'}</Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            </>
                        )}

                        <Divider />

                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="Remarks">
                                <Text>{selectedRecord.remarks || 'N/A'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Recorded By">
                                <Text>{selectedRecord.userName || 'N/A'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Created At">
                                <Text>{dayjs(selectedRecord.createdAt).format('DD/MM/YYYY HH:mm')}</Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default SlaughterWaste;
